import {
  ChangeDetectionStrategy,
  Component,
  Input,
  computed,
  signal
} from '@angular/core';

import { BarChartBlock } from '@core/services/ai/ai-assistant.service';

@Component({
  selector: 'app-ai-bar-chart-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  template: `
    @if (block.title) {
      <div class="bc__title">{{ block.title }}</div>
    }
    <div class="bc">
      @for (b of bars(); track b.label) {
        <div class="bc__row" [attr.data-tone]="b.tone || 'neutral'">
          <div class="bc__label">{{ b.label }}</div>
          <div class="bc__track">
            <div class="bc__fill" [style.width.%]="pct(b.value)"></div>
          </div>
          <div class="bc__value">{{ b.valueLabel ?? b.value }}</div>
        </div>
      }
    </div>
    @if (block.caption) {
      <div class="bc__caption">{{ block.caption }}</div>
    }
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .bc__title {
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgba(15, 23, 42, 0.55);
        margin: 0 0 8px;
      }
      .bc {
        background: #fff;
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 12px;
        padding: 10px 12px;
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .bc__row {
        display: grid;
        grid-template-columns: minmax(70px, 1fr) minmax(80px, 3fr) minmax(
            60px,
            auto
          );
        align-items: center;
        gap: 8px;
        font-size: 12px;
      }
      .bc__label {
        color: rgba(15, 23, 42, 0.7);
        overflow-wrap: anywhere;
      }
      .bc__track {
        height: 10px;
        border-radius: 999px;
        background: rgba(15, 23, 42, 0.06);
        overflow: hidden;
      }
      .bc__fill {
        height: 100%;
        background: linear-gradient(90deg, #6366f1, #8b5cf6);
        border-radius: 999px;
        min-width: 2px;
        transition: width 0.3s ease;
      }
      .bc__row[data-tone='positive'] .bc__fill {
        background: linear-gradient(90deg, #10b981, #059669);
      }
      .bc__row[data-tone='warning'] .bc__fill {
        background: linear-gradient(90deg, #f59e0b, #d97706);
      }
      .bc__row[data-tone='danger'] .bc__fill {
        background: linear-gradient(90deg, #ef4444, #dc2626);
      }
      .bc__value {
        text-align: right;
        font-weight: 600;
        color: rgba(15, 23, 42, 0.9);
        white-space: nowrap;
      }
      .bc__caption {
        margin-top: 6px;
        font-size: 12px;
        color: rgba(15, 23, 42, 0.55);
        font-style: italic;
      }
    `
  ]
})
export class AiBarChartBlockComponent {
  private readonly _block = signal<BarChartBlock | null>(null);

  @Input({ required: true })
  set block(value: BarChartBlock) {
    this._block.set(value);
  }
  get block(): BarChartBlock {
    return this._block()!;
  }

  /**
   * Defensive: richContent comes straight from Firestore docs, so a
   * malformed block (missing `bars`) must degrade to an empty chart —
   * not crash the whole message render.
   */
  readonly bars = computed<BarChartBlock['bars']>(() => {
    const b = this._block();
    return b && Array.isArray(b.bars) ? b.bars : [];
  });

  readonly maxValue = computed(() =>
    this.bars().reduce((max, x) => Math.max(max, x.value), 0)
  );

  pct(v: number): number {
    const max = this.maxValue();
    if (max <= 0) return 0;
    return Math.max(2, Math.min(100, (v / max) * 100));
  }
}
